import {
  reactive,
  ref,
  computed,
  getCurrentInstance,
  onBeforeMount,
  onMounted,
  onBeforeUnmount,
  provide,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import BpmOsgpoModule from '@/helpers/Modules/Product/BpmOsgpoModule'
import DateHelper from '@/helpers/DateHelper'
import {
  useBpmPolling,
  useBpmDraft,
  useBpmRewrite,
  useBpmScoring,
  useBpmClientVerify,
  useBpmVehicleVerify,
  useBpmWizard,
  useBpmCheckout,
  useBpmParticipants,
  useBpmInit,
  useBpmDictionaries,
  useBpmPolicyWatchers,
} from '@/composables/bpm'

export const BPM_POLICY_KEY = Symbol('bpmPolicy')
export const BPM_MODE_KEY = Symbol('bpmMode')
export const BPM_MODULE_KEY = Symbol('bpmModule')

function resolveCurrentUser (store, proxy) {
  if (store.state.user) {
    return store.state.user
  }
  const globalUser = proxy.$user
  return globalUser && typeof globalUser === 'object' ? globalUser : null
}

export function useBpmOgpo () {
  const instance = getCurrentInstance()
  const proxy = instance.proxy
  const route = useRoute()
  const router = useRouter()
  const store = useStore()

  // --- Notifications ---
  const notify = {
    error: (message) => proxy.$notifyError(message),
    success: (message, options) => proxy.$notifySuccess(message, options),
    warning: (message) => proxy.$notifyWarning(message),
  }

  // --- Core state (domain in Vuex) ---
  const policy = store.state.bpmWizard.policy

  const pollingIntervalId = ref(null)
  const currentDateIntervalId = ref(null)
  const processCheckInFlight = ref({})
  const saveDraftTimeoutId = ref(null)

  const loaded = ref(false)
  const loaderText = ref('')
  const isFormDisabled = ref(false)
  const isScoringInProgress = ref(false)
  const isGlobalLoaderVisible = ref(false)
  const isPaymentSuccessVisible = ref(false)
  const isBusinessProcessModalOpen = ref(false)
  const isBusinessProcessModalClosable = ref(true)
  const treatyDuration = ref(BpmOsgpoModule.Treaty.DEFAULT_DURATION)

  const orderDictionaries = reactive({
    managers: [],
    agents: [],
    loading: { managers: false, agents: false },
    disabled: { manager: false, agent: false },
  })

  // --- Derived state ---
  const productMode = computed(() => BpmOsgpoModule.resolveMode(route.query.type))
  const currentUser = computed(() => resolveCurrentUser(store, proxy))
  // Дата по Алматы; обновляется по таймеру, чтобы после полуночи границы дат на открытой странице не устаревали
  const currentDate = ref(DateHelper.todayIso())
  const todayIso = computed(() => currentDate.value)
  const tomorrowIsoDate = computed(() => DateHelper.addDays(currentDate.value, 1))

  const bpmContext = {
    get $route () { return route },
    get today () { return todayIso.value },
    get tomorrow () { return tomorrowIsoDate.value },
    get policy () { return policy },
  }

  const treatyMinDate = computed(() => productMode.value.getTreatyMinDate(bpmContext) ?? tomorrowIsoDate.value)
  const treatyStartDate = computed(() => productMode.value.getTreatyStartDate(bpmContext) ?? treatyMinDate.value)
  const treatyMaxEndDate = computed(() => {
    const date = policy?.treaty?.date
    return date ? BpmOsgpoModule.Treaty.maxEndDate(date, productMode.value) : ''
  })
  const treatyMinEndDate = computed(() => {
    const date = policy?.treaty?.date
    return date ? BpmOsgpoModule.Treaty.minEndDate(date, productMode.value) : ''
  })

  // Shared methods passed between composables to avoid circular imports.
  const sharedApi = {}

  // --- Feature composables ---
  const participants = useBpmParticipants({ policy, notify, sharedApi })

  const policySetup = useBpmInit({
    policy,
    mode: productMode,
    route,
    router,
    store,
    user: currentUser,
    duration: treatyDuration,
    treatyMinDate,
    treatyStartDate,
    treatyMinEndDate,
    treatyMaxEndDate,
  })

  const dictionaryActions = useBpmDictionaries({
    policy,
    user: currentUser,
    notify,
    dictionaries: orderDictionaries,
  })

  const polling = useBpmPolling({
    policy,
    mode: productMode,
    notify,
    router,
    sharedApi,
    interval: pollingIntervalId,
    processCheckInFlight,
    isBpmModalOpened: isBusinessProcessModalOpen,
    bpmModalClosable: isBusinessProcessModalClosable,
    changeDisabled: isFormDisabled,
    globalLoader: isGlobalLoaderVisible,
    isSendPayment: isPaymentSuccessVisible,
    processModalState: participants.processModalState,
  })

  const draft = useBpmDraft({
    policy,
    store,
    mode: productMode,
    route,
    router,
    notify,
    sharedApi,
    loaded,
    globalLoader: isGlobalLoaderVisible,
    treatyStartDate,
  })

  const rewrite = useBpmRewrite({
    policy,
    notify,
    sharedApi,
    loaded,
  })

  const scoring = useBpmScoring({
    policy,
    notify,
    scoringLoading: isScoringInProgress,
    changeDisabled: isFormDisabled,
  })

  const clientVerify = useBpmClientVerify({
    notify,
  })

  const vehicleVerify = useBpmVehicleVerify({
    notify,
  })

  const checkout = useBpmCheckout({
    policy,
    mode: productMode,
    notify,
    sharedApi,
    changeDisabled: isFormDisabled,
    globalLoader: isGlobalLoaderVisible,
  })

  const wizard = useBpmWizard({
    policy,
    mode: productMode,
    notify,
    scoringLoading: isScoringInProgress,
    changeDisabled: isFormDisabled,
    sharedApi,
  })

  Object.assign(
    sharedApi,
    participants,
    polling,
    draft,
    rewrite,
    scoring,
    clientVerify,
    vehicleVerify,
    checkout,
    wizard,
    policySetup,
    dictionaryActions,
    { user: currentUser },
  )

  useBpmPolicyWatchers({
    route,
    store,
    policy,
    duration: treatyDuration,
    notify,
    nextTick: proxy.$nextTick.bind(proxy),
    saveDraftTimeout: saveDraftTimeoutId,
    clientModalState: participants.clientModalState,
    sharedApi,
    treatyMinDate,
    treatyStartDate,
  })

  Object.assign(bpmContext, sharedApi, { loadRewritePolicy: rewrite.loadRewritePolicy })

  provide(BPM_POLICY_KEY, policy)
  provide(BPM_MODE_KEY, productMode)
  provide(BPM_MODULE_KEY, BpmOsgpoModule)

  // --- View model ---
  const dictionaries = computed(() => ({
    ...orderDictionaries,
    durations: BpmOsgpoModule.Treaty.durationsDictionary(productMode.value),
  }))

  const hasCalculatedPremium = computed(() => productMode.value.hasPremium(policy))

  const checkoutStatusMessage = computed(() =>
    policy.business_process.find(item => item.type === 5)?.process_data?.message
    ?? 'Начало оформления полиса',
  )

  const modalBusinessProcess = computed(() =>
    policy.business_process.find(item => item.id === participants.processModalState.id),
  )

  const switchModalInsureds = computed(() =>
    policy.insureds.filter(item => item.client.iin !== policy.insurer?.client?.iin),
  )

  const rewriteLinkedPolicies = computed(() => policy.rewrite.linked ?? [])
  const rewriteContractNumber = computed(() => policy.rewrite.contract?.global ?? '')

  // --- Template actions ---
  function openInsurerAddModal () {
    participants.openClientModal(1)
  }

  function openInsuredAddModal () {
    participants.openClientModal(2)
  }

  function setPolicyIsDrive (value) {
    policy.isDrive = value
  }

  function addEmptyDgpo () {
    policy.dgpo = BpmOsgpoModule.Dgpo.empty()
  }

  // --- Lifecycle ---
  onBeforeMount(() => {
    policySetup.redirectIfNoApc()
    policySetup.redirectIfUserNotAllowed()
    policySetup.setPolicyApc()
    policySetup.syncLayoutPageTitle()
    policySetup.applyModeTreatySettings()
  })

  onMounted(() => {
    if (route.query.draft) {
      draft.loadDraft()
    } else {
      if (route.query.ulid) {
        policySetup.resetWizard()
      }
      productMode.value.loadInitialPolicy(bpmContext)
    }
    polling.startInterval()
    currentDateIntervalId.value = setInterval(() => {
      const today = DateHelper.todayIso()
      if (today !== currentDate.value) {
        currentDate.value = today
      }
    }, 60 * 1000)
  })

  onBeforeUnmount(() => {
    polling.stopInterval()
    clearInterval(currentDateIntervalId.value)
    store.commit('setLayoutPageTitle', null)
    if (isPaymentSuccessVisible.value) {
      policySetup.resetWizard()
    }
  })

  return {
    // Module & policy
    BpmOsgpoModule,
    policy,
    productMode,

    // Page UI state
    loaded,
    loaderText,
    isFormDisabled,
    isGlobalLoaderVisible,
    isPaymentSuccessVisible,

    // Treaty step
    dictionaries,
    treatyDuration,
    treatyMinDate,
    treatyMinEndDate,
    treatyMaxEndDate,
    hasCalculatedPremium,

    // Rewrite banner
    rewriteLinkedPolicies,
    rewriteContractNumber,

    // Wizard
    wizardStep: wizard.wizardStep,
    wizardSteps: wizard.wizardSteps,
    wizardFooterHint: wizard.wizardFooterHint,
    wizardFooterShowBack: wizard.wizardFooterShowBack,
    wizardFooterPrimaryAction: wizard.wizardFooterPrimaryAction,
    isWizardStepReachable: wizard.isWizardStepReachable,
    goToPreviousWizardStep: wizard.goToPrevWizardStep,
    onWizardStepChange: wizard.onWizardStepChange,
    onWizardFooterPrimary: wizard.onWizardFooterPrimary,
    resetPremiumCalculation: wizard.handlePolicyCriticalDataChanges,

    // Participants step
    activeBusinessProcessesByType: computed(() => store.getters.bpmActiveProcessesByType),
    openInsurerAddModal,
    openInsuredAddModal,
    setPolicyIsDrive,
    addEmptyDgpo,
    openSwitchInsuredModal: participants.openSwitchInsuredModal,
    openClientModal: participants.openClientModal,
    deleteInsurer: participants.deleteInsurer,
    deleteInsured: participants.deleteInsured,
    setChangedVehicle: participants.setChangedVehicle,
    deleteVehicle: participants.deleteVehicle,
    editRejectedClient: participants.editRejectedClient,
    editRejectedVehicle: participants.editRejectedVehicle,
    switchInsurer: participants.switchInsurer,
    verifyClient: clientVerify.verifyClient,
    verifyVehicle: vehicleVerify.verifyVehicle,

    // Business processes
    changeProcessStatus: polling.changeProcessStatus,
    openBusinessProcessModal: polling.openBpmModal,
    deleteProcess: polling.deleteProcess,
    updateBusinessProcess: polling.updateProcess,
    closeBusinessProcessModal: polling.closeBpmModal,

    // Modals state
    clientModalState: participants.clientModalState,
    vehicleModalState: participants.vehicleModalState,
    clientModalExcludedClients: participants.clientModalExcludedClients,
    vehicleModalExcludedCars: participants.vehicleModalExcludedCars,
    modalBusinessProcess,
    isBusinessProcessModalClosable,
    switchModalInsureds,
    checkoutStatusMessage,

    // Modal actions
    searchClient: participants.searchClient,
    searchVehicle: participants.searchVehicle,
  }
}
